import React, { useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AgentLogo from '../../common/logo/AgentLogo';
import { useNavigate } from 'react-router-dom';

import styles from './styles/AgentIntroCard.module.css';

const AgentIntroCard = ({ agent, isExpanded = true }) => {
    const navigate = useNavigate();

    const handleCardClick = useCallback(() => {
        if (agent?.slug) {
            navigate(`/agents/${agent.slug}`);
        } else {
            navigate('/agents');
        }
    }, [navigate, agent]);

    const expandVariants = {
        initial: {
            scaleX: 0.95,
            scaleY: 0.985,
            borderRadius: '2rem',
            opacity: 0,
            y: 16,
            filter: 'blur(8px)'
        },
        expanded: {
            scaleX: 1,
            scaleY: 1,
            borderRadius: '2rem',
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            transition: {
                type: 'spring',
                stiffness: 520,
                damping: 42,
                mass: 0.9,
                opacity: { duration: 0.22 },
            }
        },
        collapsed: {
            scaleX: 0.96,
            scaleY: 0.985,
            borderRadius: '2rem',
            opacity: 0,
            y: 12,
            filter: 'blur(6px)',
            transition: {
                duration: 0.28,
                ease: [0.22, 1, 0.36, 1],
            }
        }
    };

    // Container/child variants for gentle staggered reveal
    const contentVariants = {
        initial: {
            opacity: 0,
            y: 8,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.06,
                duration: 0.22,
                ease: 'easeOut',
                when: 'beforeChildren',
                staggerChildren: 0.06,
            }
        },
        collapsed: {
            opacity: 0,
            y: 6,
            transition: {
                duration: 0.18,
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 8 },
        show: { opacity: 1, y: 0, transition: { duration: 0.22, ease: 'easeOut' } },
        exit: { opacity: 0, y: 6, transition: { duration: 0.18, ease: 'easeIn' } },
    };

    return (
        <motion.div
            className={styles.card}
            variants={expandVariants}
            initial="initial"
            animate={isExpanded ? "expanded" : "collapsed"}
            exit="collapsed"
            layout
            onClick={handleCardClick}
        >
            <motion.div
                className={styles.content}
                variants={contentVariants}
                initial="initial"
                animate={isExpanded ? "visible" : "collapsed"}
                exit="collapsed"
                layout
            >
                <motion.div variants={itemVariants} initial="hidden" animate="show" exit="exit" layout>
                    <AgentLogo
                        logo={agent.logo}
                        name={agent.name}
                        className={styles.agentLogo}
                        size={'medium'}
                    />
                </motion.div>

                <motion.div className={styles.textContent} variants={contentVariants} layout>
                    <motion.h2
                        layout="position"
                        className={styles.agentName}
                        variants={itemVariants}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                    >
                        {agent.name}
                    </motion.h2>

                    <AnimatePresence initial={false}>
                        {isExpanded && agent.description && (
                            <motion.p
                                className={styles.agentDescription}
                                variants={itemVariants}
                                initial={{ opacity: 0, height: 0, y: 6 }}
                                animate={{ opacity: 1, height: 'auto', y: 0 }}
                                exit={{ opacity: 0, height: 0, y: 4 }}
                                transition={{ duration: 0.22, ease: 'easeOut' }}
                            >
                                {agent.description}
                            </motion.p>
                        )}
                    </AnimatePresence>

                    <AnimatePresence initial={false}>
                        {isExpanded && agent.welcomeMessage && (
                            <motion.p
                                className={styles.agentWelcomeMessage}
                                variants={itemVariants}
                                initial={{ opacity: 0, height: 0, y: 8 }}
                                animate={{ opacity: 1, height: 'auto', y: 0 }}
                                exit={{ opacity: 0, height: 0, y: 6 }}
                                transition={{ duration: 0.24, ease: 'easeOut', delay: 0.04 }}
                            >
                                {agent.welcomeMessage}
                            </motion.p>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default memo(AgentIntroCard);
